<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.title"
                    :subtitle="t.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    @click="openCreate"
                >
                    <v-icon class="me-1" size="small">mdi-plus</v-icon>
                    {{ t.new }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <section class="corporate-kpi-grid" :aria-label="$t('admin.schoolAccess.summary')">
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ rows.length }}</strong>
                    <span class="corporate-kpi__label">{{ t.totalsLinks }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.courses }}</strong>
                    <span class="corporate-kpi__label">{{ t.totalsCourses }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.groups }}</strong>
                    <span class="corporate-kpi__label">{{ t.totalsGroups }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.students }}</strong>
                    <span class="corporate-kpi__label">{{ t.totalsStudents }}</span>
                </article>
            </section>

            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar school-access-toolbar">
                    <v-text-field
                        v-model="filters.search"
                        :label="t.search"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        append-inner-icon="mdi-magnify"
                        @update:modelValue="reloadDebounced"
                    />
                    <v-select
                        v-model="filters.resourceType"
                        :items="resourceTypeFilterOptions"
                        item-title="text"
                        item-value="value"
                        :label="t.filterType"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="weeduc-admin-v-field"
                        @update:modelValue="load"
                    />
                </div>

                <div class="weeduc-admin-page__table-section">
                    <div v-if="loading" class="corporate-placeholder">
                        <Loader :isActive="false" />
                    </div>
                    <div v-else-if="error" class="corporate-placeholder">{{ t.error }}</div>
                    <div v-else-if="!rows.length" class="corporate-placeholder">{{ t.empty }}</div>
                    <div v-else class="corporate-access-list">
                        <article
                            v-for="row in rows"
                            :key="row.id"
                            class="corporate-access-card"
                        >
                            <div class="corporate-access-card__main">
                                <div
                                    class="corporate-access-card__icon"
                                    :class="row.resourceType === 'group' ? 'is-group' : 'is-course'"
                                >
                                    <fa
                                        :icon="row.resourceType === 'group' ? 'fa-layer-group' : 'fa-graduation-cap'"
                                    />
                                </div>
                                <div class="corporate-access-card__body">
                                    <div class="corporate-access-card__title-row">
                                        <h3>{{ row.resourceName }}</h3>
                                        <span
                                            class="corporate-access-chip"
                                            :class="row.resourceType === 'group' ? 'is-group' : 'is-course'"
                                        >
                                            {{
                                                row.resourceType === 'group'
                                                    ? t.resourceGroup
                                                    : t.resourceCourse
                                            }}
                                        </span>
                                    </div>
                                    <div class="corporate-access-card__meta">
                                        <span>
                                            <fa icon="fa-users" />
                                            {{ formatStudentsCount(row.usersCount) }}
                                        </span>
                                        <span>
                                            <fa icon="fa-user" />
                                            {{ formatStudentLabels(row) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="corporate-access-card__actions">
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="lg"
                                    class="text-none"
                                    @click="openEdit(row)"
                                >
                                    {{ t.edit }}
                                </v-btn>
                                <v-btn
                                    size="small"
                                    variant="text"
                                    color="error"
                                    rounded="lg"
                                    class="text-none"
                                    @click="remove(row)"
                                >
                                    {{ t.remove }}
                                </v-btn>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </div>

        <v-dialog v-model="dialogOpen" max-width="640" scrollable>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">
                            {{ editingId ? t.formTitleEdit : t.formTitle }}
                        </h2>
                        <p class="corporate-dialog__subtitle">{{ t.formSubtitle }}</p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <v-select
                                v-model="form.resourceType"
                                :items="resourceTypeOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.resourceType"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                                @update:modelValue="onResourceTypeChange"
                            />
                            <v-select
                                v-if="form.resourceType === 'course'"
                                v-model="form.resourceId"
                                :items="courseOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.selectCourse"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <v-select
                                v-else
                                v-model="form.resourceId"
                                :items="groupOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.selectGroup"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <v-select
                                v-model="form.userIds"
                                :items="studentOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.students"
                                variant="outlined"
                                density="compact"
                                multiple
                                chips
                                closable-chips
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <p class="corporate-dialog__hint">{{ t.studentsHint }}</p>
                        </div>
                    </section>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="dialogOpen = false">
                        {{ t.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        @click="save"
                    >
                        {{ t.save }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import groupAssignmentsApi from '@/utils/groupAssignments';
import courseGroupsApi from '@/utils/courseGroups';
import coursesApi, { unwrapCoursesList } from '@/utils/courses';
import ApiUser from '@/utils/user';

const emptyForm = () => ({
    resourceType: 'group',
    resourceId: null,
    userIds: [],
});

export default {
    name: 'SchoolCourseAccess',
    components: { TitlePage, Loader },
    data() {
        return {
            loading: true,
            saving: false,
            error: false,
            rows: [],
            groups: [],
            courses: [],
            students: [],
            dialogOpen: false,
            editingId: null,
            form: emptyForm(),
            searchTimeout: null,
            filters: {
                search: '',
                resourceType: null,
            },
        };
    },
    computed: {
        ...mapState(['token']),
        t() {
            return {
                title: this.$t('admin.schoolAccess.titleFull'),
                subtitle: this.$t('admin.schoolAccess.subtitleFull'),
                new: this.$t('admin.schoolAccess.new'),
                search: this.$t('admin.schoolAccess.searchFull'),
                filterType: this.$t('admin.schoolAccess.filterType'),
                empty: this.$t('admin.schoolAccess.empty'),
                error: this.$t('admin.schoolAccess.error'),
                edit: this.$t('common.edit'),
                remove: this.$t('admin.schoolAccess.remove'),
                cancel: this.$t('common.cancel'),
                save: this.$t('common.save'),
                formTitle: this.$t('admin.schoolAccess.formTitle'),
                formTitleEdit: this.$t('admin.schoolAccess.formTitleEdit'),
                formSubtitle: this.$t('admin.schoolAccess.formSubtitle'),
                sectionData: this.$t('admin.schoolAccess.sectionData'),
                resourceType: this.$t('admin.schoolAccess.resourceType'),
                resourceCourse: this.$t('admin.schoolAccess.resourceCourse'),
                resourceGroup: this.$t('admin.schoolAccess.resourceGroup'),
                selectCourse: this.$t('admin.schoolAccess.selectCourse'),
                selectGroup: this.$t('admin.schoolAccess.selectGroup'),
                students: this.$t('admin.schoolAccess.students'),
                studentsHint: this.$t('admin.schoolAccess.studentsHint'),
                studentsCount: this.$t('admin.schoolAccess.studentsCount'),
                all: this.$t('admin.schoolAccess.all'),
                totalsLinks: this.$t('admin.schoolAccess.totalsLinks'),
                totalsCourses: this.$t('admin.schoolAccess.totalsCourses'),
                totalsGroups: this.$t('admin.schoolAccess.totalsGroups'),
                totalsStudents: this.$t('admin.schoolAccess.totalsStudentsUnique'),
                resourceRequired: this.$t('admin.schoolAccess.resourceRequired'),
                studentsRequired: this.$t('admin.schoolAccess.studentsRequired'),
                removeConfirm: this.$t('admin.schoolAccess.removeConfirm'),
            };
        },
        stats() {
            const courses = new Set(
                this.rows.filter((r) => r.resourceType === 'course').map((r) => r.resourceId),
            ).size;
            const groups = new Set(
                this.rows.filter((r) => r.resourceType === 'group').map((r) => r.resourceId),
            ).size;
            const students = new Set(this.rows.flatMap((r) => r.userIds || [])).size;
            return { courses, groups, students };
        },
        resourceTypeOptions() {
            return [
                { text: this.t.resourceCourse, value: 'course' },
                { text: this.t.resourceGroup, value: 'group' },
            ];
        },
        resourceTypeFilterOptions() {
            return [{ text: this.t.all, value: null }, ...this.resourceTypeOptions];
        },
        groupOptions() {
            return (this.groups || [])
                .filter((g) => g.status !== 'inactive')
                .map((g) => ({
                    text: g.name,
                    value: String(g.id),
                    courseIds: g.courseIds || [],
                }));
        },
        courseOptions() {
            return (this.courses || []).map((c) => ({
                text: c.name || c.comercialName || `Curso ${c.id}`,
                value: String(c.id),
            }));
        },
        studentOptions() {
            return (this.students || []).map((user) => ({
                text: user.email ? `${user.name} (${user.email})` : user.name,
                value: String(user.id),
            }));
        },
        groupMap() {
            return Object.fromEntries(
                (this.groups || []).map((g) => [String(g.id), g]),
            );
        },
        courseMap() {
            return Object.fromEntries(
                (this.courses || []).map((c) => [
                    String(c.id),
                    { ...c, name: c.name || c.comercialName },
                ]),
            );
        },
        userMap() {
            return Object.fromEntries(
                (this.students || []).map((u) => [String(u.id), u]),
            );
        },
    },
    async created() {
        await Promise.all([this.loadGroups(), this.loadCourses(), this.loadStudents()]);
        await this.load();
    },
    beforeDestroy() {
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
    },
    methods: {
        formatStudentsCount(count) {
            return this.t.studentsCount.replace('{n}', String(count || 0));
        },
        formatStudentLabels(row) {
            const labels = row.userLabels?.length
                ? row.userLabels
                : (row.userIds || []).map((id) => this.userMap[String(id)]?.name || id);
            if (!labels.length) return '—';
            if (labels.length <= 3) return labels.join(', ');
            return `${labels.slice(0, 3).join(', ')} +${labels.length - 3}`;
        },
        async loadGroups() {
            try {
                const response = await courseGroupsApi.list(this.token, {});
                this.groups = response.content || [];
            } catch (e) {
                this.groups = [];
            }
        },
        async loadCourses() {
            try {
                const response = await coursesApi.getDataCourse(this.token);
                this.courses = unwrapCoursesList(response);
            } catch (e) {
                this.courses = [];
            }
        },
        async loadStudents() {
            try {
                const response = await ApiUser.getAllUser(this.token, 0, 9999, '', 1, 3);
                const list = response?.content || response || [];
                this.students = Array.isArray(list) ? list : [];
            } catch (e) {
                this.students = [];
            }
        },
        reloadDebounced() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.load(), 300);
        },
        async load() {
            this.loading = true;
            this.error = false;
            try {
                const response = await groupAssignmentsApi.list(this.token, {
                    search: this.filters.search || undefined,
                    resourceType: this.filters.resourceType || undefined,
                    groupMap: this.groupMap,
                    courseMap: this.courseMap,
                    userMap: this.userMap,
                });
                this.rows = response.content || [];
            } catch (e) {
                this.error = true;
                this.rows = [];
            } finally {
                this.loading = false;
            }
        },
        openCreate() {
            this.editingId = null;
            this.form = emptyForm();
            this.dialogOpen = true;
        },
        openEdit(row) {
            this.editingId = row.id;
            this.form = {
                resourceType: row.resourceType || 'group',
                resourceId: String(row.resourceId || row.groupId),
                userIds: [...(row.userIds || [])].map(String),
            };
            this.dialogOpen = true;
        },
        onResourceTypeChange() {
            this.form.resourceId = null;
        },
        resolveCourseIds() {
            if (this.form.resourceType === 'course') {
                return this.form.resourceId ? [String(this.form.resourceId)] : [];
            }
            const group = this.groupMap[String(this.form.resourceId)];
            return (group?.courseIds || []).map(String);
        },
        async enrolUsers(userIds, courseIds) {
            if (!courseIds.length || !userIds.length) return;
            const jobs = [];
            userIds.forEach((userId) => {
                courseIds.forEach((courseId) => {
                    jobs.push(
                        coursesApi.manualEnrol(this.token, {
                            tokenStudent: userId,
                            tokenCourse: courseId,
                        }).catch((error) => {
                            console.warn('[school-access] falha ao matricular', {
                                userId,
                                courseId,
                                error,
                            });
                        }),
                    );
                });
            });
            await Promise.all(jobs);
        },
        async save() {
            if (!this.form.resourceId) {
                alert(this.t.resourceRequired);
                return;
            }
            if (!this.form.userIds?.length) {
                alert(this.t.studentsRequired);
                return;
            }
            this.saving = true;
            try {
                const payload = {
                    resourceType: this.form.resourceType,
                    resourceId: String(this.form.resourceId),
                    groupId: this.form.resourceType === 'group'
                        ? String(this.form.resourceId)
                        : null,
                    userIds: this.form.userIds.map(String),
                };
                if (this.editingId) {
                    await groupAssignmentsApi.update(this.editingId, payload, this.token);
                } else {
                    await groupAssignmentsApi.create(payload, this.token);
                }
                await this.enrolUsers(payload.userIds, this.resolveCourseIds());
                this.dialogOpen = false;
                await this.load();
            } catch (e) {
                alert(this.t.error);
            } finally {
                this.saving = false;
            }
        },
        async remove(row) {
            if (!window.confirm(this.t.removeConfirm)) return;
            try {
                await groupAssignmentsApi.remove(row.id, this.token);
                await this.load();
            } catch (e) {
                alert(this.t.error);
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.school-access-toolbar {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
}

@media (min-width: 960px) {
    .school-access-toolbar {
        grid-template-columns: minmax(200px, 1.5fr) minmax(160px, 220px);
        align-items: end;
    }
}

.corporate-access-list {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.corporate-access-card {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    background: #fff;
}

.corporate-access-card__main {
    display: flex;
    gap: 0.9rem;
    min-width: 0;
    flex: 1;
}

.corporate-access-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #eef1fb;
    color: #3b3f8c;
}

.corporate-access-card__icon.is-group {
    background: #e8f5f0;
    color: #1f7a57;
}

.corporate-access-card__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.4rem;
}

.corporate-access-card__title-row h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #1a1d26;
}

.corporate-access-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
}

.corporate-access-chip.is-course {
    background: #eef1fb;
    color: #3b3f8c;
}

.corporate-access-chip.is-group {
    background: #e8f5f0;
    color: #1f7a57;
}

.corporate-access-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.1rem;
    font-size: 0.82rem;
    color: #5c6370;
}

.corporate-access-card__meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}

.corporate-access-card__actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}
</style>
